import type { CurrentSession } from "@cleanbudget/contract/session";
import { Alert, AlertDescription } from "@cleanbudget/ui/components/alert";
import { Button } from "@cleanbudget/ui/components/button";
import { Separator } from "@cleanbudget/ui/components/separator";
import { Skeleton } from "@cleanbudget/ui/components/skeleton";
import { IconAlertCircle, IconFingerprint, IconKey, IconLoader2 } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useLocation, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthLayout } from "@/components/auth-layout";
import { NewPasswordFields } from "@/components/sign-in/new-password";
import { apiErrorTag } from "@/lib/api-error";
import { orpc } from "@/lib/orpc";
import { type Problem, problemOf } from "@/lib/passkey-problem";
import { sessionQuery } from "@/lib/session";
import { createPasskey } from "@/lib/webauthn";

/**
 * The enrolment link (`/enrol#<token>`, made by `bun run employee:create` or an invitation): attach
 * a passkey (recommended) or set a password (ADR 0003), and be signed in. The token stays in the
 * fragment, which never reaches a server log.
 */
export const Route = createFileRoute("/enrol")({
	component: EnrolPage,
});

function EnrolPage() {
	const { t } = useTranslation("session");
	const { t: tCommon } = useTranslation();
	const token = useLocation({ select: (location) => location.hash.replace(/^#/, "") });
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [problem, setProblem] = useState<Problem>();
	const [waiting, setWaiting] = useState(false);

	const options = useQuery({
		...orpc.session.enrolmentOptions.queryOptions({ input: { token } }),
		enabled: token !== "",
		staleTime: 4 * 60_000,
		refetchInterval: 4 * 60_000,
		refetchOnWindowFocus: false,
		retry: false,
	});
	const enrol = useMutation(orpc.session.enrol.mutationOptions());
	const enrolPassword = useMutation(orpc.session.enrolPassword.mutationOptions());
	const [choosingPassword, setChoosingPassword] = useState(false);
	const linkInvalid =
		token === "" ||
		apiErrorTag(options.error) === "EnrolmentLinkInvalid" ||
		apiErrorTag(enrol.error) === "EnrolmentLinkInvalid" ||
		apiErrorTag(enrolPassword.error) === "EnrolmentLinkInvalid";

	const signedIn = async (session: CurrentSession) => {
		queryClient.setQueryData(sessionQuery().queryKey, session);
		// Replace, so the used link is not left in the history.
		await navigate({ to: "/", replace: true });
	};

	const attach = async () => {
		if (!options.data) return;
		setProblem(undefined);
		setWaiting(true);
		try {
			const credential = await createPasskey(options.data.publicKey);
			await signedIn(await enrol.mutateAsync({ token, challengeId: options.data.challengeId, credential }));
		} catch (error) {
			setProblem(problemOf(error));
			void options.refetch();
		} finally {
			setWaiting(false);
		}
	};

	if (linkInvalid) {
		return <AuthLayout title={t("enrol.invalid.title")} description={t("enrol.invalid.description")} />;
	}
	if (!options.data) {
		return (
			<AuthLayout title={t("enrol.title")}>
				{options.error ? (
					<Alert variant="destructive">
						<IconAlertCircle aria-hidden />
						<AlertDescription>{t(`problem.${problemOf(options.error)}`)}</AlertDescription>
					</Alert>
				) : (
					<Skeleton className="h-10 w-full" />
				)}
			</AuthLayout>
		);
	}
	return (
		<AuthLayout
			title={t("enrol.welcome", { name: options.data.employee.name })}
			description={t("enrol.description", { email: options.data.employee.email })}
		>
			<section className="flex flex-col gap-3" aria-labelledby="enrol-passkey">
				<h2 id="enrol-passkey" className="font-heading text-base font-medium">
					{t("enrol.passkey.title")}
				</h2>
				<p className="text-sm text-muted-foreground">{t("enrol.passkey.description")}</p>
				{problem ? (
					<Alert variant="destructive">
						<IconAlertCircle aria-hidden />
						<AlertDescription>{t(`problem.${problem}`)}</AlertDescription>
					</Alert>
				) : null}
				<Button size="lg" onClick={attach} disabled={waiting}>
					{waiting ? <IconLoader2 className="animate-spin" aria-hidden /> : <IconFingerprint aria-hidden />}
					{waiting ? t("enrol.busy") : t("enrol.passkey.button")}
				</Button>
			</section>
			<Separator />
			<section className="flex flex-col gap-3" aria-labelledby="enrol-password">
				<h2 id="enrol-password" className="font-heading text-base font-medium">
					{t("enrol.password.title")}
				</h2>
				<p className="text-sm text-muted-foreground">{t("enrol.password.description")}</p>
				{choosingPassword ? (
					<NewPasswordFields
						submitLabel={t("enrol.password.button")}
						otherError={(error) =>
							apiErrorTag(error) === "RateLimited" ? t("problem.tooMany") : tCommon("form.failed")
						}
						onSave={async (password) => signedIn(await enrolPassword.mutateAsync({ token, password }))}
					/>
				) : (
					<Button size="lg" variant="outline" onClick={() => setChoosingPassword(true)}>
						<IconKey aria-hidden />
						{t("enrol.password.button")}
					</Button>
				)}
			</section>
		</AuthLayout>
	);
}
